import type { ProjectType } from "../projects/types";
import { sanitizeLayerName } from "./layerNames";
import {
  MAX_OBJECT_LAYERS,
  MAX_RASTER_LAYERS,
  MAX_TOTAL_LAYERS,
  isLayerRole,
  type PersistedLayerState,
} from "./layerTypes";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export interface ValidatedLayerDocument {
  layers: PersistedLayerState[];
  activeLayerId: string;
  dialogueLayerId?: string;
}

export function validateLayerDocument(
  rawLayers: unknown,
  rawActiveLayerId: unknown,
  projectType: ProjectType,
): ValidatedLayerDocument {
  if (!Array.isArray(rawLayers) || rawLayers.length === 0 || rawLayers.length > MAX_TOTAL_LAYERS) {
    throw new Error("The layer stack is invalid.");
  }
  const ids = new Set<string>();
  const orders = new Set<number>();
  const layers = rawLayers.map((raw): PersistedLayerState => {
    if (
      !isRecord(raw) ||
      typeof raw.id !== "string" ||
      raw.id.length === 0 ||
      ids.has(raw.id) ||
      (raw.type !== "raster" && raw.type !== "object") ||
      !isLayerRole(raw.role) ||
      typeof raw.system !== "boolean" ||
      typeof raw.visible !== "boolean" ||
      typeof raw.locked !== "boolean" ||
      typeof raw.opacity !== "number" ||
      !Number.isFinite(raw.opacity) ||
      raw.opacity < 0 ||
      raw.opacity > 1 ||
      !Number.isInteger(raw.order) ||
      (raw.order as number) < 0 ||
      orders.has(raw.order as number)
    ) {
      throw new Error("Layer metadata is invalid.");
    }
    const name = sanitizeLayerName(raw.name);
    if (!name) throw new Error("Layer names cannot be empty.");
    if (raw.type === "object" && raw.role !== "dialogue") {
      throw new Error("The object layer role is invalid.");
    }
    if (raw.type === "object" && (!raw.system || name !== "Dialogue")) {
      throw new Error("The Dialogue system layer is invalid.");
    }
    if (raw.type === "raster" && raw.system) {
      throw new Error("Raster layers cannot be system layers.");
    }
    if (raw.type === "raster" && raw.role === "dialogue") {
      throw new Error("A raster layer cannot be Dialogue.");
    }
    ids.add(raw.id);
    orders.add(raw.order as number);
    return {
      id: raw.id,
      name,
      type: raw.type,
      role: raw.role,
      system: raw.system,
      visible: raw.visible,
      locked: raw.locked,
      opacity: raw.opacity,
      order: raw.order as number,
    };
  });
  const sorted = layers.sort((left, right) => left.order - right.order);
  if (sorted.some((layer, index) => layer.order !== index)) {
    throw new Error("Layer order must be contiguous.");
  }
  const firstNonBackground = sorted.findIndex((layer) => layer.role !== "background");
  if (
    firstNonBackground >= 0 &&
    sorted.slice(firstNonBackground).some((layer) => layer.role === "background")
  ) {
    throw new Error("Background layers must remain behind other layers.");
  }
  const rasterLayers = sorted.filter((layer) => layer.type === "raster");
  const objectLayers = sorted.filter((layer) => layer.type === "object");
  if (rasterLayers.length < 1 || rasterLayers.length > MAX_RASTER_LAYERS) {
    throw new Error("The raster layer count is invalid.");
  }
  if (objectLayers.length > MAX_OBJECT_LAYERS) throw new Error("Too many object layers.");
  if (projectType === "drawing" && objectLayers.length !== 0) {
    throw new Error("Drawing projects cannot contain Dialogue.");
  }
  if (projectType === "comic" && objectLayers.length !== 1) {
    throw new Error("Comic projects require exactly one Dialogue layer.");
  }
  if (typeof rawActiveLayerId !== "string" || !ids.has(rawActiveLayerId)) {
    throw new Error("The active layer is invalid.");
  }
  return {
    layers: sorted.map((layer) => ({ ...layer })),
    activeLayerId: rawActiveLayerId,
    dialogueLayerId: objectLayers[0]?.id,
  };
}

export function normalizeLayerOrder(
  layers: ReadonlyArray<PersistedLayerState>,
): PersistedLayerState[] {
  return [...layers]
    .sort((left, right) => left.order - right.order)
    .map((layer, order) => ({ ...layer, order }));
}
